import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import { EAccountRole, TSignedInAccount } from 'types/GlobalAccount'
import { selectCustomer } from './Customer'
import { AllowAcess, Customer } from 'types/GlobalCustomer'

type AccountLoadingFalse = {
  account: TSignedInAccount
  loading: false
}

type AccountLoadingTrue = {
  account: null
  loading: true
}

type AccountSettings = {
  allowAccess: AllowAcess
  allowAiChat: boolean
  allowCreateDashboard: boolean
  allowCreateReport: boolean
  allowEarlyAccessExportTables: boolean
  dashboardLimitReached: boolean
  hasIdp: boolean
  isAccessHub: boolean
  isAdmin: boolean
  isCompanyBased: boolean
  isCompanyGroup: boolean
  isEconomist: boolean
  isHomepal: boolean
  isInProductTeam: boolean
  numberOfDashboardsInLicense: number
  numberOfDashboardsInUse: number
  isManagementCompany: boolean
}

export type UseAccountSettingsReturn = AccountSettings &
  (AccountLoadingFalse | AccountLoadingTrue)

const EMPTY_ALLOW_ACCESS: AllowAcess = {
  white_label_colors: false,
  white_label_logotype: false,
  insight_studio: false,
  access_hub: false,
  sso: false,
  frequency_of_data_synchronization: null,
  support_level: null,
  embed: false,
  export_tables: false,
  allowed_historical_time_period: 3,
  allow_currency_converter: false,
  allow_financial_year: false,
  allow_key_metrics: false,
  allow_mcp: false,
  allow_reports: false,
  allow_errand_ai_safety_measures: false,
  allow_errand_ai_error_source: false,
  allow_errand_ai_water_leak: false,
  allow_ticket_ai_safety_measures: false,
  allow_ticket_ai_error_source: false,
  allow_ticket_ai_water_leak: false,
  internal_follow_up: false,
  allow_ai_content_chat: false,
  allow_analyses: false
}

function getDashboardLimitReached(
  isHomepal: boolean,
  customer: Customer | null
) {
  if (isHomepal) {
    return false
  }

  const numberOfDashboardsInLicense = customer?.number_of_dashboards || 0
  const numberOfDashboardsInUse = customer?.number_of_dashboards_in_use || 0

  return numberOfDashboardsInLicense
    ? numberOfDashboardsInUse >= numberOfDashboardsInLicense
    : false
}

// select current account
export const selectSignedInAccount = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AuthStore.account
  ],
  (data) => data
)

export const selectAzureAd = createSelector(
  [(state: ApplicationState) => state.AuthStore.azureAd],
  (azureAd) => azureAd
)

export const selectIsAuthenticated = createSelector(
  [(state: ApplicationState) => state.AuthStore.isAuthenticated],
  (isAuthenticated) => isAuthenticated
)

export const selectIsValidatingUser = createSelector(
  [(state: ApplicationState) => state.AuthStore.validating],
  (validating) => validating
)

export const selectAccountSettings = createSelector(
  [selectCustomer, selectSignedInAccount],
  (customer, account): UseAccountSettingsReturn => {
    const isCompanyBased =
      customer?.organizational_unit_access_type === 'company'

    if (!customer || !account) {
      return {
        account: null,
        allowAccess: EMPTY_ALLOW_ACCESS,
        allowAiChat: false,
        allowCreateDashboard: false,
        allowCreateReport: false,
        allowEarlyAccessExportTables: false,
        dashboardLimitReached: false,
        hasIdp: false,
        isAccessHub: false,
        isAdmin: false,
        isCompanyBased,
        isCompanyGroup: false,
        isEconomist: false,
        isHomepal: false,
        isInProductTeam: false,
        loading: true,
        numberOfDashboardsInLicense: 0,
        numberOfDashboardsInUse: 0,
        isManagementCompany: false
      }
    }

    const isAdmin =
      account.role === EAccountRole.ADMIN || account.is_homepal_user

    return {
      account,
      allowAccess: customer.allow_access,
      allowAiChat: account.allow_ai_chat && !account.is_company_group,
      allowCreateDashboard: !account.is_company_group,
      allowCreateReport: customer.allow_access.allow_reports && isAdmin,
      allowEarlyAccessExportTables: account.allow_early_access_export_tables,
      dashboardLimitReached: getDashboardLimitReached(
        account.is_homepal_user,
        customer
      ),
      hasIdp: customer.has_idp && !account.is_homepal_user,
      isAccessHub: account.role === EAccountRole.ACCESS_HUB,
      isAdmin,
      isCompanyBased,
      isCompanyGroup: account.is_company_group,
      isEconomist: account.has_access_to_economy_settings,
      isHomepal: account.is_homepal_user,
      isInProductTeam: account.is_in_product_team,
      loading: false,
      numberOfDashboardsInLicense: customer.number_of_dashboards || 0,
      numberOfDashboardsInUse: customer.number_of_dashboards_in_use,
      isManagementCompany: customer.is_management_company
    }
  }
)
